import { randomBytes } from 'crypto';

/**
 * Recursively adds missing keys from template into target.
 * Never overwrites existing values - only fills gaps.
 *
 * @returns List of added key paths (e.g. ["Entra", "Email.AzureAcs.Region"])
 */
export function addMissingKeys(
  target: Record<string, unknown>,
  template: Record<string, unknown>,
  prefix = '',
): string[] {
  const added: string[] = [];

  for (const key of Object.keys(template)) {
    const path = prefix ? `${prefix}.${key}` : key;

    if (!(key in target)) {
      // Key missing entirely - add the whole branch
      target[key] = template[key];
      added.push(path);
    } else if (
      isPlainObject(target[key]) &&
      isPlainObject(template[key])
    ) {
      // Both are objects - recurse to find nested missing keys
      const nested = addMissingKeys(
        target[key] as Record<string, unknown>,
        template[key] as Record<string, unknown>,
        path,
      );
      added.push(...nested);
    }
    // else: key exists with a scalar/array value - preserve client's value
  }

  return added;
}

/**
 * Replace template placeholders with project-specific values.
 * Same logic as init.ts template replacement.
 */
export function resolveTemplatePlaceholders(
  content: string,
  projectName: string,
): string {
  return content
    .replace(/\{\{ProjectName\}\}/g, projectName)
    .replace(/\{\{ProjectDomain\}\}/g, `${projectName.toLowerCase()}.app`)
    .replace(/\{\{ProjectNameLower\}\}/g, projectName.toLowerCase())
    .replace(/\{\{JwtSecret\}\}/g, randomBytes(64).toString('base64'));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Builds the appsettings.json `ss init` writes: the template, with placeholders
 * resolved and the chosen email provider.
 *
 * It carries NO JWT signing secret: appsettings.json is committed and baked into
 * the docker image, so a secret written there travels with every clone and every
 * image. Each runtime gets its own instead — `ss dev configure` writes it to the
 * gitignored appsettings.Local.json, `ss init` / `ss secrets dataprotection
 * --docker` to docker-images/.env (compose maps it onto Jwt__Secret), and a
 * deployment injects Jwt__Secret from its vault. With none, the platform signs
 * with an ephemeral RSA key (dev only: tokens do not survive a restart).
 */
export function buildInitialAppSettings(
  templateContent: string,
  projectName: string,
  emailProvider: string,
): Record<string, unknown> {
  const resolved = templateContent
    .replace(/\{\{ProjectName\}\}/g, projectName)
    .replace(/\{\{ProjectDomain\}\}/g, `${projectName.toLowerCase()}.app`)
    .replace(/\{\{ProjectNameLower\}\}/g, projectName.toLowerCase());

  const appSettings = JSON.parse(resolved) as Record<string, unknown>;

  const email = ensureObject(appSettings, 'Email');
  email.Provider = emailProvider;
  if (emailProvider === 'Disabled') {
    email.Enabled = false;
  }

  return appSettings;
}

/**
 * Config paths the platform has DEPRECATED or RELOCATED. `ss upgrade` removes
 * them so an existing project converges to the current
 * appsettings.json.template (which mirrors the SmartStack.app binding contract).
 *
 * This is a CURATED whitelist — never "every key absent from the template" — so
 * a user's own added sections/keys are never touched. When the app drops or
 * relocates a bound section, add its OLD path here.
 */
export const OBSOLETE_PATHS: readonly (readonly string[])[] = [
  // EntraSso moved to Authentication:EntraSso; the top-level node is dead/unbound.
  ['EntraSso'],
  // RestrictExternalAppsToDataApi never had a binding. Only the KEY is pruned — since
  // v3.65 the SmartStack:Security node hosts MfaBreakGlass (the admin lockout escape
  // hatch), so removing the whole subsection would silently disarm a configured
  // break-glass on every `ss upgrade`.
  ['SmartStack', 'Security', 'RestrictExternalAppsToDataApi'],
  // AutoMigrate / FailOnMigrationError were removed from SmartStackOptions in v3.65:
  // migrations are applied unconditionally at startup and a failure is fatal. The
  // template dropped them (0c21f4d) — prune them from existing client configs too.
  ['SmartStack', 'AutoMigrate'],
  ['SmartStack', 'FailOnMigrationError'],
  // IntegrationOptions dropped these three in v3.65; nothing reads them anymore.
  ['Integrations', 'SchedulerEvaluationIntervalSeconds'],
  ['Integrations', 'SinkBatchSize'],
  ['Integrations', 'DefaultTimeoutSeconds'],
  // Zefix + UidWebservice credentials moved to the DB (ExternalApiConnection); the
  // API reads neither section — leaving them invites editing config that does nothing.
  ['Zefix'],
  ['UidWebservice'],
  // Never bound: the API has no GetSection("MultiTenant"), and GetFeatureConfigQuery
  // hardcodes MultiTenantEnabled = true. Injected by an old `ss init`.
  ['MultiTenant'],
  // NOTE: Authentication:EntraSso:Authority was listed here until package 3.66.0, when it was
  // genuinely unbound. It is now a REAL setting — the authority served to the browser through
  // GET /api/config/features so MSAL knows which tenant to sign in against. Pruning it would strip
  // a working single-tenant SSO configuration on every `ss upgrade`, and the symptom would appear
  // later, in the browser, on Microsoft's domain. Do not put it back.
  // SessionSettings has no CleanupBatchSize; the cleanup job does not page.
  ['Session', 'CleanupBatchSize'],
  // AutoAssignmentSettings binds neither: SLA pre-breach escalation and workload
  // rebalancing were never implemented.
  ['AutoAssignment', 'SlaPreBreachThresholdPercent'],
  ['AutoAssignment', 'EnableWorkloadRebalancing'],
];

/**
 * Removes deprecated config paths (default: OBSOLETE_PATHS) from `target`, in
 * place. A path that is absent is skipped. After a removal, any ancestor object
 * left empty is pruned too. Returns the dotted paths actually removed.
 */
export function removeObsoleteKeys(
  target: Record<string, unknown>,
  obsoletePaths: readonly (readonly string[])[] = OBSOLETE_PATHS,
): string[] {
  const removed: string[] = [];
  for (const path of obsoletePaths) {
    if (deleteAtPath(target, path)) {
      removed.push(path.join('.'));
    }
  }
  return removed;
}

/**
 * Deletes the value at `path` if present, walking down only through plain
 * objects. Prunes ancestor objects left empty by the deletion. Returns true iff
 * a value was removed.
 */
function deleteAtPath(root: Record<string, unknown>, path: readonly string[]): boolean {
  if (path.length === 0) return false;

  const chain: Array<{ obj: Record<string, unknown>; key: string }> = [];
  let current: Record<string, unknown> = root;
  for (let i = 0; i < path.length - 1; i++) {
    const next = current[path[i]];
    if (!isPlainObject(next)) return false; // path does not exist
    chain.push({ obj: current, key: path[i] });
    current = next;
  }

  const leaf = path[path.length - 1];
  if (!(leaf in current)) return false;
  delete current[leaf];

  // Prune now-empty ancestors, deepest first.
  for (let i = chain.length - 1; i >= 0; i--) {
    const { obj, key } = chain[i];
    const child = obj[key];
    if (isPlainObject(child) && Object.keys(child).length === 0) {
      delete obj[key];
    } else {
      break;
    }
  }

  return true;
}

/**
 * Some sections were RELOCATED, not merely dropped. Before removeObsoleteKeys
 * prunes the dead top-level node, `ss upgrade` moves a project's *configured*
 * values to the new bound location so the section survives the move instead of
 * being silently lost. Curated (like OBSOLETE_PATHS); a destination value
 * already present is never overwritten — the newer config wins.
 *
 * Currently one relocation, mirroring the SmartStack.app binding change:
 *   EntraSso (top-level)        → Authentication:EntraSso
 *   EntraSso.AllowedTenantIds   → Authentication:Microsoft:AllowedTenants
 * The tenant whitelist is FOLDED into Microsoft because EntraSsoSettings dropped
 * AllowedTenantIds (the exact same mapping the login-config CLI applies).
 *
 * Runs BEFORE addMissingKeys + removeObsoleteKeys. Returns the OLD dotted paths
 * whose values were actually relocated (empty when nothing needed moving).
 */
export function relocateLegacyKeys(target: Record<string, unknown>): string[] {
  const moved: string[] = [];

  const legacyEntra = target.EntraSso;
  if (isPlainObject(legacyEntra)) {
    const auth = ensureObject(target, 'Authentication');
    let relocatedAny = false;
    for (const [key, value] of Object.entries(legacyEntra)) {
      if (key === 'AllowedTenantIds') {
        // Folded to Authentication:Microsoft:AllowedTenants (only if unset there).
        const microsoft = ensureObject(auth, 'Microsoft');
        if (!('AllowedTenants' in microsoft)) {
          microsoft.AllowedTenants = value;
          relocatedAny = true;
        }
      } else {
        // Fill the new EntraSso location without clobbering an existing value.
        const dest = ensureObject(auth, 'EntraSso');
        if (!(key in dest)) {
          dest[key] = value;
          relocatedAny = true;
        }
      }
    }
    if (relocatedAny) moved.push('EntraSso');
  }

  return moved;
}

/**
 * A value the TEMPLATE shipped wrong, and what it should have been. `ss upgrade`
 * rewrites it only on an EXACT match of what we shipped — a value the client
 * typed themselves is theirs, whatever it is.
 */
export interface ShippedDefaultCorrection {
  readonly path: readonly string[];
  readonly shipped: string;
  readonly corrected: string;
}

/**
 * Curated, like OBSOLETE_PATHS. addMissingKeys never overwrites, so without this
 * a defect in a shipped default lives forever in every project generated before
 * the fix.
 */
export const SHIPPED_DEFAULT_CORRECTIONS: readonly ShippedDefaultCorrection[] = [
  // TimeSpan.Parse reads "24:00:00" as 24 DAYS (the hour field stops at 23), so
  // every generated project mailed confirmation links valid 24 days — and the mail
  // said so in plain text. The intent was always 24 hours. Template fixed in
  // 7c392f43; SmartStack.app 0facd7bd warns at startup on lifetimes read as days.
  {
    path: ['Email', 'TokenExpiration', 'EmailConfirmation'],
    shipped: '24:00:00',
    corrected: '1.00:00:00',
  },
];

/**
 * Applies SHIPPED_DEFAULT_CORRECTIONS to `target`, in place. Never creates a
 * path, never touches a value that differs from the shipped one. Returns one
 * human-readable line per correction actually made.
 */
export function correctShippedDefaults(
  target: Record<string, unknown>,
  corrections: readonly ShippedDefaultCorrection[] = SHIPPED_DEFAULT_CORRECTIONS,
): string[] {
  const corrected: string[] = [];

  for (const { path, shipped, corrected: value } of corrections) {
    let parent: Record<string, unknown> = target;
    let reachable = true;
    for (const segment of path.slice(0, -1)) {
      const next = parent[segment];
      if (!isPlainObject(next)) {
        reachable = false;
        break;
      }
      parent = next;
    }

    const leaf = path[path.length - 1];
    if (reachable && parent[leaf] === shipped) {
      parent[leaf] = value;
      corrected.push(`${path.join('.')}: "${shipped}" → "${value}"`);
    }
  }

  return corrected;
}

/** Return parent[key] as a plain object, creating an empty one if absent/not-object. */
function ensureObject(
  parent: Record<string, unknown>,
  key: string,
): Record<string, unknown> {
  const current = parent[key];
  if (isPlainObject(current)) return current;
  const created: Record<string, unknown> = {};
  parent[key] = created;
  return created;
}
